import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AgentForm } from './components/AgentForm';
import { CreateAgentData } from './types';
import { PageLoader } from '../../components';
import { useAgents } from './hooks';
import { useToast } from '../../contexts/useToast';
import { useAuth } from '../Auth';

const EditAgent: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [initialData, setInitialData] = useState<CreateAgentData | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { fetchAgent, updateAgent } = useAgents();
  const { showToast } = useToast();
  const { refreshOrganization } = useAuth();

  useEffect(() => {
    const loadAgent = async () => {
      if (!id || hasLoaded) return;

      setLoading(true);
      setError(null);

      try {
        // Refresh organization data in parallel with agent fetch
        const [agent] = await Promise.all([fetchAgent(id), refreshOrganization()]);
        if (agent) {
          const agentData: CreateAgentData = {
            name: agent.name,
            voice: agent.voice.id,
            knowledge_base: agent.knowledge_base?.id || null,
            documents: agent.documents.map(doc => doc.id),
            call_routes: agent.call_routes.map(route => route.id),
            phone_number: agent.phone_number?.id || null,
            intro_message: agent.intro_message,
            prompt: agent.prompt || agent.system_prompt || '',
            status: agent.status,
            variable_extraction: agent.variable_extraction,
            web_search_enabled: agent.web_search_enabled,
            all_internet: agent.all_internet || false,
            whitelist_websites: agent.whitelist_websites,
            live_api_calls_enabled: agent.live_api_calls_enabled,
            recording_notice_enabled: agent.recording_notice_enabled ?? true,
            live_api_calls: agent.live_api_calls.map(call => ({
              method: call.method,
              name: 'name' in call ? (call as { name?: string }).name || '' : '',
              url: call.url,
              headers: call.headers,
              body: call.body,
              call_type: call.call_type,
            })),
            intelligent_conversation_summary: agent.intelligent_conversation_summary,
            follow_up_enabled: agent.follow_up_enabled,
            post_call_api_request_enabled: agent.post_call_api_request_enabled,
            post_api_calls: agent.post_api_calls.map(call => ({
              method: call.method,
              name: 'name' in call ? (call as { name?: string }).name || '' : '',
              url: call.url,
              headers: call.headers,
              body: call.body,
              call_type: call.call_type,
            })),
            max_call_time_minutes: agent.max_call_time_minutes,
            max_waiting_time_seconds: agent.max_waiting_time_seconds,
            vad_type: agent.vad_type || 'server_vad',
            noise_reduction_mode: agent.noise_reduction_mode || 'off',
            threshold: agent.threshold,
            prefix_padding: agent.prefix_padding,
            silence: agent.silence,
            creativity: agent.creativity,
            call_recording: agent.call_recording || 'disabled',
            working_hours: agent.working_hours || '24/7',
            priority_level: agent.priority_level || 'medium',
            tags: agent.tags || '',
            calendar_integration:
              Array.isArray(agent.calendars) && agent.calendars.length > 0
                ? typeof agent.calendars[0] === 'string'
                  ? agent.calendars[0]
                  : agent.calendars[0].id
                : '',
            email_template: agent.email_template?.id || null,
            email_follow_up_subject: agent.email_follow_up_subject || '',
            email_follow_up_content: agent.email_follow_up_content || '',
            email_follow_up_timing: agent.email_follow_up_timing || 'immediately',
            email_follow_up_recipients: Array.isArray(agent.email_follow_up_recipients)
              ? agent.email_follow_up_recipients
              : [],
            sms_template: agent.sms_template?.id || null,
            sms_follow_up_content: agent.sms_follow_up_content || '',
            sms_follow_up_timing: agent.sms_follow_up_timing || 'immediately',
            sms_follow_up_recipients: agent.sms_follow_up_recipients || [],
          };

          setInitialData(agentData);
          setHasLoaded(true);
        } else {
          setError(t('agents.agentNotFound'));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : t('agents.agentLoadFailed'));
      } finally {
        setLoading(false);
      }
    };

    loadAgent();
  }, [id, fetchAgent, hasLoaded, t, refreshOrganization]);

  const handleSave = async (data: CreateAgentData) => {
    if (!id) return;

    try {
      const result = await updateAgent(id, data);

      if (result.success) {
        showToast({
          type: 'success',
          title: t('agents.agentUpdatedSuccessTitle'),
          message: t('agents.agentUpdatedSuccessMessage', { name: data.name }),
          duration: 5000,
        });
      }

      return result;
    } catch (err) {
      return {
        success: false,
        errors: {
          fieldErrors: {},
          nonFieldErrors: [],
          generalError: err instanceof Error ? err.message : t('agents.agentUpdateFailedMessage'),
        },
      };
    }
  };

  if (loading) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-red-500 text-center">
          <p className="text-lg font-semibold">{t('agents.errorLoadingAgent')}</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!initialData) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500 text-center">
          <p className="text-lg font-semibold">{t('agents.agentNotFound')}</p>
        </div>
      </div>
    );
  }

  return (
    <AgentForm
      mode="edit"
      initialData={initialData}
      agentId={id || undefined}
      onSave={handleSave}
    />
  );
};

export { EditAgent };
